import { DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";

import { ProgressButton, useToast, DialogRoot } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";

import { CartSearchInput } from "~/features/cartSearchInput/CartSearchInput";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import {
  mixpanelTrack,
  mixpanelPickingConfirmCartWrapper,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { setCartNumberConfirmed } from "~/redux/actions";
import { selectBatchWithCartNumber } from "~/redux/selectors/batchSelectors";
import {
  selectSelectedPortId,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { useClosePortMutation } from "~/redux/warehouse/autostoreGrid.hooks";

type CartNumberConfirmationModalProps = {
  maxWidth?: "xs" | "sm" | "md" | "lg";
  resetState: () => void;
};

export function CartNumberConfirmationModal(
  props: CartNumberConfirmationModalProps
) {
  const { maxWidth, resetState } = props;
  const { t } = useTranslation();
  const { enableColorChanges } = useFlag();
  const navigate = useFcNavigate();
  const dispatch = useAppDispatch();
  const batchWithCartNumber = useAppSelector(selectBatchWithCartNumber);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const portId = useAppSelector(selectSelectedPortId);

  const { successToast, errorToast } = useToast();
  const [closePort] = useClosePortMutation();
  const [preppedCartNumber, setPreppedCartNumber] = useState<string | null>(
    null
  );
  const trackedModalName = useMixpanelModalName("Picking Confirm Cart");

  const onCancelCallback = async () => {
    try {
      if (autostoreGridId && portId) {
        await closePort({
          autostoreGridId,
          portId
        }).unwrap();
        successToast("Port closed.");
        navigate("/autostore-main");
      }
    } catch (err) {
      errorToast(getMessageFromRtkError(err), {
        trackingLocation: trackedModalName
      });
    }
  };

  if (!batchWithCartNumber?.cartNumber) return <></>;

  return (
    <DialogRoot open maxWidth={maxWidth}>
      <DialogTitle>
        {t("bring cart")}
        {batchWithCartNumber?.cartNumber
          ? ` ${batchWithCartNumber?.cartNumber} `
          : " "}
        {t("to the port")}
      </DialogTitle>

      <DialogContent>
        <Grid container direction="column">
          <Grid item style={{ marginTop: 50 }}>
            <CartSearchInput
              setPreppedCartNumberCb={(cartNumber1) => {
                setPreppedCartNumber(cartNumber1);
              }}
              placeholder={t("scan cart to confirm")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: 3,
          gap: "10px",
          flexWrap: "nowrap"
        }}
      >
        <ProgressButton
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Button Click",
              label: "Cancel"
            });
            resetState();
            void onCancelCallback();
          }}
          buttonSize="medium"
          emphasis="high"
          responsive
          variant="contained"
          color={enableColorChanges ? "secondary" : "primary"}
          style={{ width: 130, minWidth: 0, maxWidth: "100%" }}
        >
          {t("cancel")}
        </ProgressButton>

        <ProgressButton
          onClick={() => {
            mixpanelPickingConfirmCartWrapper(
              trackedModalName,
              "Button Click",
              "Confirm",
              {
                cartNumber: batchWithCartNumber?.cartNumber || "",
                preppedCartNumber
              }
            );

            if (
              preppedCartNumber &&
              preppedCartNumber === batchWithCartNumber?.cartNumber
            ) {
              // Input number matches cart number
              successToast(
                `Successfully confirmed cart: ${batchWithCartNumber.cartNumber}`,
                { duration: 1000 }
              );
              dispatch(setCartNumberConfirmed(true));
            } else {
              errorToast("Invalid cart number", {
                trackingLocation: trackedModalName
              });
            }
          }}
          buttonSize="medium"
          emphasis="high"
          responsive
          variant="contained"
          color="primary"
          style={{ width: 130, minWidth: 0, maxWidth: "100%" }}
        >
          {t("confirm")}
        </ProgressButton>
      </DialogActions>
    </DialogRoot>
  );
}
